import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get timeline and goal score from state, URL params, or localStorage
  const urlParams = new URLSearchParams(location.search);
  const timeline = location.state?.timeline || urlParams.get('timeline') || localStorage.getItem('selectedTimeline') || 6;
  const goalScore = location.state?.goalScore || urlParams.get('goal') || localStorage.getItem('selectedGoalScore') || 720;
  const monthlyBudget = location.state?.monthlyBudget || urlParams.get('budget') || localStorage.getItem('monthlyBudget') || 300;
  
  // Session ID is optional - may not exist yet if coming directly from landing
  const stateSessionId = location.state?.sessionId;
  const urlSessionId = urlParams.get('session');
  const storedSessionId = localStorage.getItem('creditSessionId');
  const sessionId = stateSessionId || urlSessionId || storedSessionId;
  
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Beta code state
  const [betaCode, setBetaCode] = useState('');
  const [showBetaInput, setShowBetaInput] = useState(false);
  const [betaLoading, setBetaLoading] = useState(false);
  const [betaError, setBetaError] = useState('');

  const handleCheckout = async () => {
    if (!firstName.trim()) {
      setError('Please enter your first name');
      return;
    }

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Store name, email and timeline for later
      localStorage.setItem('userName', firstName.trim());
      localStorage.setItem('userEmail', email);
      localStorage.setItem('selectedTimeline', timeline);
      localStorage.setItem('monthlyBudget', monthlyBudget);
      
      // Create a pre-purchase session for tracking
      const response = await axios.post(`${API}/checkout/create-prepurchase`, {
        email: email,
        first_name: firstName.trim(),
        timeline_months: parseInt(timeline),
        origin_url: window.location.origin
      });

      if (response.data.url) {
        // Store the prepurchase ID for after payment
        localStorage.setItem('prepurchaseId', response.data.prepurchase_id);
        window.location.href = response.data.url;
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleBetaUnlock = async () => {
    if (!firstName.trim()) {
      setBetaError('Please enter your first name above');
      return;
    }
    
    if (!betaCode.trim()) {
      setBetaError('Please enter a beta code');
      return;
    }
    
    // Store the user's name for beta users too
    localStorage.setItem('userName', firstName.trim());
    
    if (!sessionId) {
      // For beta users without a session, go to upload page after validation
      setBetaLoading(true);
      setBetaError('');
      
      try {
        const response = await axios.post(`${API}/beta-validate`, {
          beta_code: betaCode.trim()
        });
        
        if (response.data.valid) {
          const validBetaCode = betaCode.trim().toUpperCase();
          localStorage.setItem('betaCode', validBetaCode);
          localStorage.setItem('selectedTimeline', timeline);
          localStorage.setItem('selectedGoalScore', goalScore);
          localStorage.setItem('monthlyBudget', monthlyBudget);
          localStorage.setItem('userName', firstName.trim());
          // Beta users go to upload their report - pass the actual beta code
          navigate('/upload', { 
            state: { 
              timeline, 
              goalScore,
              monthlyBudget, 
              betaAccess: validBetaCode 
            } 
          });
        }
      } catch (error) {
        setBetaError(error.response?.data?.detail || 'Invalid beta code');
      } finally {
        setBetaLoading(false);
      }
      return;
    }
    
    setBetaLoading(true);
    setBetaError('');
    
    try {
      const response = await axios.post(`${API}/beta-unlock`, {
        session_id: sessionId,
        beta_code: betaCode.trim()
      });
      
      if (response.data.success) {
        localStorage.setItem('creditSessionId', sessionId);
        navigate('/upsell', { state: { sessionId, fromBeta: true } });
      }
    } catch (error) {
      setBetaError(error.response?.data?.detail || 'Invalid beta code');
    } finally {
      setBetaLoading(false);
    }
  };

  return (
    <div className="checkout-container">
      <div className="checkout-box">
        {/* Header */}
        <div className="checkout-header">
          <h1 className="checkout-title">Complete Your Plan</h1>
          <p className="checkout-subtitle">
            You're one step away from your personalized credit roadmap.
          </p>
        </div>

        {/* Product Summary */}
        <div className="checkout-product">
          <div className="product-name">
            Credit GPS Plan — {timeline} Month Timeline
          </div>
          
          <ul className="product-features">
            <li>
              <span className="feature-check">✓</span>
              Personalized step-by-step action plan
            </li>
            <li>
              <span className="feature-check">✓</span>
              What to do first, next, and later
            </li>
            <li>
              <span className="feature-check">✓</span>
              Timeline-based strategy
            </li>
            <li>
              <span className="feature-check">✓</span>
              Instant access
            </li>
          </ul>
        </div>

        {/* Pricing */}
        <div className="checkout-pricing">
          <div className="price-amount">$29<span className="price-cents">.99</span></div>
          <div className="price-type">one-time</div>
          <p className="price-note">No subscriptions. No hidden fees.</p>
        </div>

        {/* Trust Elements */}
        <div className="checkout-trust">
          <div className="trust-item">
            <span className="trust-icon">🔒</span>
            <span>Secure payment (Stripe)</span>
          </div>
          <div className="trust-item">
            <span className="trust-icon">⚡</span>
            <span>Instant access after purchase</span>
          </div>
          <div className="trust-item">
            <span className="trust-icon">✓</span>
            <span>No recurring charges</span>
          </div>
          <div className="trust-item">
            <span className="trust-icon">📚</span>
            <span>Educational guidance only</span>
          </div>
        </div>

        {/* Name & Email Input */}
        <div className="checkout-form">
          <label className="form-label">First Name</label>
          <input
            type="text"
            placeholder="Your first name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="form-input"
            data-testid="checkout-name-input"
          />
          
          <label className="form-label" style={{ marginTop: '16px' }}>Email Address</label>
          <input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="form-input"
            data-testid="checkout-email-input"
          />
          {error && <p className="form-error">{error}</p>}
        </div>

        {/* CTA Button */}
        <button
          className="checkout-btn"
          onClick={handleCheckout}
          disabled={loading}
          data-testid="checkout-submit-btn"
        >
          {loading ? 'Processing...' : 'Unlock My Plan'}
        </button>

        {/* Risk Reversal */}
        <p className="checkout-guarantee">
          If you don't find value, reach out — we'll make it right.
        </p>

        {/* Beta Code Section - Always Visible */}
        <div className="checkout-beta-section-visible">
          <div className="beta-divider">
            <span>OR</span>
          </div>
          <div className="beta-code-box">
            <label className="beta-label">Have a beta or founder code?</label>
            <div className="beta-input-row">
              <input
                type="text"
                placeholder="ENTER CODE"
                value={betaCode}
                onChange={(e) => setBetaCode(e.target.value.toUpperCase())}
                className="beta-code-input-large"
                data-testid="beta-code-input"
              />
              <button
                onClick={handleBetaUnlock}
                disabled={betaLoading}
                className="beta-apply-btn-large"
                data-testid="beta-submit-btn"
              >
                {betaLoading ? '...' : 'Apply'}
              </button>
            </div>
            {betaError && <p className="beta-error-msg">{betaError}</p>}
          </div>
        </div>

        {/* Stripe Badge */}
        <div className="stripe-badge">
          <span>Powered by</span>
          <svg viewBox="0 0 60 25" className="stripe-logo">
            <path fill="#635BFF" d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.02 1.04-.06 1.48zm-3.67-3.14c0-1.3-.7-2.3-1.96-2.3-1.26 0-2.15 1.04-2.33 2.3h4.29zm-15.81 9.06h4.15V5.5h-4.15v14.7zm-4.83-14.7h-4.15v1.82c-.84-1.4-2.33-2.15-4.05-2.15-3.6 0-6.2 3.07-6.2 7.43 0 4.5 2.6 7.58 6.37 7.58 1.64 0 3.1-.73 3.97-2.15v1.8h4.07V5.5zm-6.55 11.23c-1.9 0-3.1-1.5-3.1-3.8 0-2.23 1.15-3.72 3.1-3.72 1.82 0 3.06 1.5 3.06 3.72-.04 2.3-1.24 3.8-3.06 3.8zm-11.95 3.48h4.1V5.5h-4.1v14.7zm-4.84-14.7h-4.15v1.82c-.84-1.4-2.33-2.15-4.05-2.15-3.6 0-6.2 3.07-6.2 7.43 0 4.5 2.6 7.58 6.37 7.58 1.64 0 3.1-.73 3.97-2.15v1.8h4.07V5.5zm-6.55 11.23c-1.9 0-3.1-1.5-3.1-3.8 0-2.23 1.15-3.72 3.1-3.72 1.82 0 3.06 1.5 3.06 3.72-.04 2.3-1.24 3.8-3.06 3.8z"/>
          </svg>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
